import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Menu, X, LogOut } from "lucide-react";
import { useAdminAuth } from "./AuthContext";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: "LayoutDashboard" },
  { to: "/admin/enquiries", label: "Enquiries", icon: "MessageSquareText" },
  { to: "/admin/applications", label: "Applications", icon: "FileText" },
  { to: "/admin/college-requests", label: "College Requests", icon: "Building2" },
  { to: "/admin/contact-messages", label: "Contact Messages", icon: "Mail" },
  { to: "/admin/course-recommendations", label: "Course Recommendations", icon: "Sparkles" },
  { to: "/admin/career-applications", label: "Career Applications", icon: "Briefcase" },
  { to: "/admin/chatbot", label: "Chatbot Q&A", icon: "MessageCircle" },
  { to: "/admin/students", label: "Students", icon: "Users" },
  { to: "/admin/programs", label: "Programs", icon: "GraduationCap" },
  { to: "/admin/courses", label: "Courses", icon: "BookOpen" },
  { to: "/admin/results", label: "Results", icon: "Trophy" },
  { to: "/admin/content", label: "Content & Settings", icon: "Settings" },
  { to: "/admin/gallery", label: "Gallery", icon: "Images" },
  { to: "/admin/blog", label: "Blog", icon: "Newspaper" },
  { to: "/admin/testimonials", label: "Testimonials", icon: "Quote" },
  { to: "/admin/events", label: "Events", icon: "CalendarDays" },
  { to: "/admin/announcements", label: "Announcements", icon: "Megaphone" },
  { to: "/admin/support", label: "Support", icon: "LifeBuoy" },
  { to: "/admin/payments", label: "Payments", icon: "CreditCard" },
  { to: "/admin/team", label: "Team", icon: "UserCog" },
];

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAdminAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-orange text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
    }`;

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 px-5 py-5">
        <span className="font-heading text-2xl font-bold text-white">ASAI</span>
        <span className="rounded bg-orange/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-orange">Admin</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-6">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`adminnav-${n.to.split("/").pop()}`} onClick={() => setOpen(false)}>
            <Icon name={n.icon} className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="mb-3 truncate text-xs text-slate-400">{user?.email}</div>
        <button onClick={doLogout} data-testid="admin-logout" className="flex w-full items-center gap-2 rounded-lg bg-white/5 px-3 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:bg-white/10">
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: "Satoshi, sans-serif" }}>
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 bg-slate-950 lg:block">{SidebarInner}</aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setOpen(false)} />
          <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-slate-950 lg:hidden">{SidebarInner}</aside>
        </>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-8">
          <button onClick={() => setOpen(true)} className="rounded-lg p-2 text-slate-700 lg:hidden" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden text-sm font-medium text-slate-500 lg:block">ASAI Admin Console</div>
          <div className="text-sm text-slate-600">{user?.name}</div>
        </header>
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};
